import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Users,
    TrendingUp,
    CalendarCheck,
    Trophy,
    LogOut,
    Trash2,
    ExternalLink,
    Mail,
    Phone,
    Flag,
    Wallet,
    Clock,
    Flame,
    Download,
    NotebookPen,
    Check,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const STAGES = [
    "New Lead",
    "Contacted",
    "Scheduled Visit",
    "Attended",
    "Negotiation",
    "Closed",
];

const STAGE_COLOR = {
    "New Lead": "bg-sky-500/10 text-sky-400 border-sky-500/30",
    "Contacted": "bg-indigo-500/10 text-indigo-400 border-indigo-500/30",
    "Scheduled Visit": "bg-amber-500/10 text-amber-400 border-amber-500/30",
    "Attended": "bg-violet-500/10 text-violet-400 border-violet-500/30",
    "Negotiation": "bg-orange-500/10 text-orange-400 border-orange-500/30",
    "Closed": "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
};

export default function AdminDashboard() {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hotOnly, setHotOnly] = useState(false);
    const navigate = useNavigate();
    const adminEmail = localStorage.getItem("wynwood_admin_email");

    const load = useCallback(async () => {
        try {
            const [leadsRes, statsRes] = await Promise.all([
                api.get("/admin/leads"),
                api.get("/admin/stats"),
            ]);
            setLeads(leadsRes.data);
            setStats(statsRes.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                localStorage.removeItem("wynwood_admin_token");
                navigate("/admin/login");
                return;
            }
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        load();
    }, [load]);

    const logout = () => {
        localStorage.removeItem("wynwood_admin_token");
        localStorage.removeItem("wynwood_admin_email");
        navigate("/admin/login");
    };

    const updateLead = async (leadId, patch) => {
        try {
            const { data } = await api.patch(`/admin/leads/${leadId}`, patch);
            setLeads((all) => all.map((l) => (l.id === leadId ? data : l)));
            load();
            return data;
        } catch (err) {
            toast.error(formatApiError(err));
            throw err;
        }
    };

    const deleteLead = async (leadId) => {
        if (!window.confirm("Delete this lead?")) return;
        try {
            await api.delete(`/admin/leads/${leadId}`);
            setLeads((all) => all.filter((l) => l.id !== leadId));
            load();
            toast.success("Lead deleted");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const exportCsv = async () => {
        try {
            const token = localStorage.getItem("wynwood_admin_token");
            const res = await fetch(`${API}/admin/leads.csv`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Export failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `wynwood_leads_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch (err) {
            toast.error(err.message || "Export failed");
        }
    };

    const filtered = useMemo(
        () => (hotOnly ? leads.filter((l) => l.is_hot) : leads),
        [leads, hotOnly],
    );

    const byStage = useMemo(() => {
        const map = Object.fromEntries(STAGES.map((s) => [s, []]));
        filtered.forEach((l) => {
            (map[l.stage] || (map[l.stage] = [])).push(l);
        });
        return map;
    }, [filtered]);

    return (
        <div className="min-h-screen bg-charcoal text-white">
            <header className="sticky top-0 z-40 bg-charcoal/80 backdrop-blur-xl border-b border-white/10">
                <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="dashboard-brand">
                        <span className="w-7 h-7 rounded-sm bg-gold flex items-center justify-center text-charcoal font-display font-black text-xs">
                            W
                        </span>
                        <div className="leading-none">
                            <div className="font-display text-sm font-bold tracking-wide">
                                WYNWOOD CRM
                            </div>
                            <div className="text-[10px] tracking-[0.25em] uppercase text-white/40">
                                Admin Console
                            </div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-4">
                        <span className="hidden md:inline text-xs text-white/50">
                            {adminEmail}
                        </span>
                        <button
                            onClick={exportCsv}
                            className="text-[11px] tracking-[0.25em] uppercase text-white/50 hover:text-gold inline-flex items-center gap-2"
                            data-testid="export-csv-btn"
                        >
                            <Download size={12} /> Export CSV
                        </button>
                        <Link
                            to="/"
                            className="text-[11px] tracking-[0.25em] uppercase text-white/50 hover:text-gold inline-flex items-center gap-2"
                        >
                            <ExternalLink size={12} /> View Site
                        </Link>
                        <button
                            onClick={logout}
                            className="text-[11px] tracking-[0.25em] uppercase text-white/50 hover:text-red-400 inline-flex items-center gap-2"
                            data-testid="admin-logout-btn"
                        >
                            <LogOut size={12} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-[1600px] px-6 md:px-10 py-10">
                <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">
                            Pipeline Overview
                        </div>
                        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
                            Leads Dashboard
                        </h1>
                    </div>
                    <button
                        onClick={() => setHotOnly((v) => !v)}
                        className={`inline-flex items-center gap-2 px-4 py-2 border text-xs tracking-[0.2em] uppercase transition-colors ${
                            hotOnly
                                ? "bg-red-500/10 border-red-500/50 text-red-400"
                                : "border-white/15 text-white/60 hover:border-gold/50"
                        }`}
                        data-testid="hot-filter-btn"
                    >
                        <Flame size={14} />
                        {hotOnly ? "Showing hot only" : "Show hot only"}
                    </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
                    <KPI icon={Users} label="Total Leads" value={stats?.total ?? 0} testId="kpi-total" />
                    <KPI icon={Flame} label="Hot Leads" value={stats?.hot_leads ?? 0} testId="kpi-hot" highlight />
                    <KPI icon={TrendingUp} label="Contacted" value={stats?.by_stage?.["Contacted"] ?? 0} testId="kpi-contacted" />
                    <KPI icon={CalendarCheck} label="Tours / Pipeline" value={stats?.tours_booked ?? 0} testId="kpi-tours" />
                    <KPI icon={Trophy} label="Conversion" value={`${stats?.conversion_rate ?? 0}%`} testId="kpi-conversion" />
                </div>

                {loading ? (
                    <div className="text-white/50 text-sm">Loading leads…</div>
                ) : filtered.length === 0 ? (
                    <EmptyState hotOnly={hotOnly} />
                ) : (
                    <div
                        className="grid grid-flow-col auto-cols-[minmax(280px,1fr)] gap-4 overflow-x-auto pb-6"
                        data-testid="kanban-board"
                    >
                        {STAGES.map((stage) => (
                            <div
                                key={stage}
                                className="bg-charcoal-surface border border-white/10 rounded-md flex flex-col max-h-[75vh]"
                                data-testid={`kanban-col-${stage.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                                    <span
                                        className={`text-[10px] tracking-[0.25em] uppercase px-2 py-1 border ${STAGE_COLOR[stage]}`}
                                    >
                                        {stage}
                                    </span>
                                    <span className="text-xs text-white/40">
                                        {byStage[stage]?.length || 0}
                                    </span>
                                </div>
                                <div className="p-3 space-y-3 overflow-y-auto flex-1">
                                    {(byStage[stage] || []).map((lead) => (
                                        <LeadCard
                                            key={lead.id}
                                            lead={lead}
                                            onChangeStage={(s) => updateLead(lead.id, { stage: s }).then(() => toast.success(`Moved to ${s}`))}
                                            onSaveNotes={(n) => updateLead(lead.id, { notes: n }).then(() => toast.success("Notes saved"))}
                                            onDelete={() => deleteLead(lead.id)}
                                        />
                                    ))}
                                    {(!byStage[stage] || byStage[stage].length === 0) && (
                                        <div className="text-xs text-white/30 italic text-center py-6">
                                            No leads
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

function KPI({ icon: Icon, label, value, testId, highlight }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-5 border rounded-md ${
                highlight
                    ? "border-red-500/30 bg-red-500/5"
                    : "border-white/10 bg-charcoal-surface"
            }`}
            data-testid={testId}
        >
            <Icon
                size={16}
                className={highlight ? "text-red-400 mb-4" : "text-gold mb-4"}
            />
            <div className="font-display text-3xl font-bold tracking-tight">
                {value}
            </div>
            <div className="mt-1 text-[10px] tracking-[0.3em] uppercase text-white/40">
                {label}
            </div>
        </motion.div>
    );
}

function LeadCard({ lead, onChangeStage, onSaveNotes, onDelete }) {
    const [editingNotes, setEditingNotes] = useState(false);
    const [notesDraft, setNotesDraft] = useState(lead.notes || "");

    const saveNotes = async () => {
        try {
            await onSaveNotes(notesDraft || null);
            setEditingNotes(false);
        } catch (e) {
            // toast already shown
        }
    };

    return (
        <div
            className={`p-4 bg-charcoal border rounded-sm transition-colors ${
                lead.is_hot
                    ? "border-red-500/40 hover:border-red-500/70"
                    : "border-white/10 hover:border-gold/40"
            }`}
            data-testid={`lead-card-${lead.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <div className="font-display font-semibold text-sm truncate">
                            {lead.name}
                        </div>
                        {lead.is_hot && (
                            <span
                                className="inline-flex items-center gap-1 bg-red-500/15 border border-red-500/40 text-red-400 px-1.5 py-0.5 text-[9px] tracking-[0.2em] uppercase font-bold"
                                data-testid={`hot-badge-${lead.id}`}
                            >
                                <Flame size={9} /> HOT
                            </span>
                        )}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-[11px] text-white/50 truncate">
                        <Mail size={10} /> {lead.email}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-[11px] text-white/50 truncate">
                        <Phone size={10} /> {lead.phone}
                    </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                    <div className="text-[10px] tracking-[0.15em] uppercase text-white/40">
                        Score
                    </div>
                    <div
                        className={`font-display text-base font-bold ${
                            lead.is_hot ? "text-red-400" : "text-gold"
                        }`}
                    >
                        {lead.score ?? 0}
                    </div>
                </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
                <Tag>{lead.investment_intent}</Tag>
                <Tag>{lead.preferred_experience}</Tag>
            </div>

            {(lead.budget || lead.timeline || lead.nationality) && (
                <div className="mt-3 grid grid-cols-1 gap-1 text-[11px] text-white/50">
                    {lead.budget && (
                        <div className="flex items-center gap-1.5">
                            <Wallet size={10} /> {lead.budget}
                        </div>
                    )}
                    {lead.timeline && (
                        <div className="flex items-center gap-1.5">
                            <Clock size={10} /> {lead.timeline}
                        </div>
                    )}
                    {lead.nationality && (
                        <div className="flex items-center gap-1.5">
                            <Flag size={10} /> {lead.nationality}
                        </div>
                    )}
                </div>
            )}

            {/* Notes editor */}
            <div className="mt-3 border-t border-white/5 pt-3">
                {editingNotes ? (
                    <div>
                        <Textarea
                            value={notesDraft}
                            onChange={(e) => setNotesDraft(e.target.value)}
                            rows={3}
                            className="bg-transparent border-white/20 text-xs rounded-none resize-none focus-visible:ring-gold"
                            placeholder="Internal notes…"
                            data-testid={`lead-notes-textarea-${lead.id}`}
                        />
                        <div className="mt-2 flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    setNotesDraft(lead.notes || "");
                                    setEditingNotes(false);
                                }}
                                className="text-white/40 hover:text-white"
                                data-testid={`lead-notes-cancel-${lead.id}`}
                            >
                                <X size={14} />
                            </button>
                            <button
                                onClick={saveNotes}
                                className="text-gold hover:text-gold-hover"
                                data-testid={`lead-notes-save-${lead.id}`}
                            >
                                <Check size={14} />
                            </button>
                        </div>
                    </div>
                ) : (
                    <button
                        onClick={() => setEditingNotes(true)}
                        className="w-full text-left text-[11px] text-white/50 hover:text-white flex items-start gap-2"
                        data-testid={`lead-notes-edit-${lead.id}`}
                    >
                        <NotebookPen size={11} className="mt-0.5 shrink-0" />
                        <span className="italic">
                            {lead.notes ? lead.notes : "Add internal notes…"}
                        </span>
                    </button>
                )}
            </div>

            <div className="mt-3 flex items-center gap-2">
                <Select value={lead.stage} onValueChange={onChangeStage}>
                    <SelectTrigger
                        className="flex-1 bg-transparent border-white/20 h-9 text-xs rounded-none"
                        data-testid={`lead-stage-select-${lead.id}`}
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-charcoal-surface border-white/20 text-white">
                        {STAGES.map((s) => (
                            <SelectItem key={s} value={s}>
                                {s}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <button
                    onClick={onDelete}
                    className="text-white/30 hover:text-red-400 shrink-0 p-2"
                    aria-label="Delete lead"
                    data-testid={`lead-delete-${lead.id}`}
                >
                    <Trash2 size={14} />
                </button>
            </div>

            <div className="mt-3 text-[10px] text-white/30">
                {new Date(lead.created_at).toLocaleString()}
            </div>
        </div>
    );
}

function Tag({ children }) {
    return (
        <span className="inline-block px-2 py-0.5 border border-white/15 text-[10px] tracking-[0.15em] uppercase text-white/60">
            {children}
        </span>
    );
}

function EmptyState({ hotOnly }) {
    return (
        <div className="p-16 border border-dashed border-white/10 text-center">
            <div className="text-[11px] tracking-[0.3em] uppercase text-gold">
                {hotOnly ? "No hot leads yet" : "Waiting for leads"}
            </div>
            <div className="mt-4 font-display text-2xl">
                {hotOnly ? "Nothing scoring 75+ right now." : "Your pipeline is empty."}
            </div>
            <p className="mt-3 text-white/50 max-w-md mx-auto text-sm">
                {hotOnly
                    ? "Toggle the filter off to see every lead in the pipeline."
                    : "As soon as visitors submit the landing page form, they'll appear here automatically in \"New Lead\"."}
            </p>
        </div>
    );
}
